/**
 * Artifact classification helpers for the viewer overlay layer.
 */

export type ArtifactOverlayType =
  | "muscle"
  | "eye"
  | "electrode_pop"
  | "flatline"
  | "high_impedance"
  | "movement"
  | "noise";

export const ARTIFACT_OVERLAY_TYPES: ArtifactOverlayType[] = [
  "muscle",
  "eye",
  "electrode_pop",
  "flatline",
  "high_impedance",
  "movement",
  "noise",
];

export const ARTIFACT_OVERLAY_LABELS: Record<ArtifactOverlayType, string> = {
  muscle: "Muscle",
  eye: "Eye",
  electrode_pop: "Electrode",
  flatline: "Flatline",
  high_impedance: "High impedance",
  movement: "Movement",
  noise: "Noise",
};

export type ClassifiedArtifact = {
  id: string;
  type: ArtifactOverlayType;
  startTimeSeconds: number;
  endTimeSeconds: number;
  channels: string[];
  confidence: number;
  label: string;
};

export function mapDetectorTypeToOverlay(
  type: string,
): ArtifactOverlayType | null {
  const t = String(type || "").toLowerCase();
  if (t === "movement" || t.includes("movement") || t.includes("motion")) {
    return "movement";
  }
  if (
    t.includes("noise") ||
    t === "line_noise" ||
    t.includes("powerline") ||
    t.includes("60hz") ||
    t.includes("50hz")
  ) {
    return "noise";
  }
  if (t === "emg" || t.includes("muscle")) return "muscle";
  if (t === "eog" || t.includes("eye") || t.includes("blink")) return "eye";
  if (
    t.includes("electrode") ||
    t.includes("pop") ||
    t === "saturation" ||
    t.includes("drift")
  ) {
    return "electrode_pop";
  }
  if (
    t.includes("flat") ||
    t.includes("disconnect") ||
    t.includes("flatline")
  ) {
    return "flatline";
  }
  if (t.includes("imped")) {
    return "high_impedance";
  }
  return null;
}

export function filterArtifactsByControls(
  artifacts: ClassifiedArtifact[],
  options: {
    enabledTypes: Set<ArtifactOverlayType> | ArtifactOverlayType[];
    confidenceThreshold: number;
  },
): ClassifiedArtifact[] {
  const enabled =
    options.enabledTypes instanceof Set
      ? options.enabledTypes
      : new Set(options.enabledTypes);
  const threshold = Math.max(0, Math.min(1, options.confidenceThreshold));
  return artifacts.filter(
    (item) =>
      enabled.has(item.type) &&
      item.confidence >= threshold &&
      Number.isFinite(item.startTimeSeconds),
  );
}

export function classifyChannelQuality(
  snrDb: number | null | undefined,
  impedanceKohms: number | null | undefined,
): "good" | "fair" | "poor" | "unknown" {
  if (
    (snrDb == null || !Number.isFinite(snrDb)) &&
    (impedanceKohms == null || !Number.isFinite(impedanceKohms))
  ) {
    return "unknown";
  }
  const snr = snrDb ?? 20;
  const z = impedanceKohms ?? 5;
  if (snr >= 20 && z <= 5) return "good";
  if (snr >= 10 && z <= 10) return "fair";
  return "poor";
}

export function toSearchableAnnotationPayload(
  artifact: ClassifiedArtifact,
): Record<string, unknown> {
  return {
    kind: "artifact",
    artifact_type: artifact.type,
    confidence: artifact.confidence,
    channels: artifact.channels,
    evidence_snippet: `${ARTIFACT_OVERLAY_LABELS[artifact.type]} (${Math.round(artifact.confidence * 100)}%)`,
  };
}
